import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testPrepAPI } from '../components/test-prep/testPrepApi';

const ACCENT = '#1e3a5f';
const ACCENT_LIGHT = '#2a5080';
const SUCCESS_GREEN = '#27ae60';
const BORDER_GRAY = '#dce1e8';
const BG_LIGHT = '#f4f6f9';
const TEXT_DARK = '#1a1a2e';
const TEXT_MID = '#5a6170';
const WHITE = '#ffffff';

const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: TEXT_DARK,
    background: BG_LIGHT,
  },
  header: {
    background: ACCENT,
    color: WHITE,
    padding: '28px 40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  backBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: 'none',
    color: WHITE,
    padding: '8px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  headerTitle: { fontSize: 22, fontWeight: 700 },
  content: { maxWidth: 960, margin: '0 auto', padding: '32px 24px' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: ACCENT, marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 },
  card: {
    background: WHITE,
    borderRadius: 10,
    border: `1px solid ${BORDER_GRAY}`,
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    cursor: 'pointer',
  },
  cardTitle: { fontSize: 16, fontWeight: 700, color: TEXT_DARK, marginBottom: 12 },
  cardMeta: { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  metaBadge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 20,
    background: BG_LIGHT,
    color: TEXT_MID,
  },
  startBtn: {
    width: '100%',
    padding: '10px 0',
    borderRadius: 6,
    border: 'none',
    background: ACCENT,
    color: WHITE,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  historyTable: {
    width: '100%',
    borderCollapse: 'collapse',
    background: WHITE,
    borderRadius: 10,
    overflow: 'hidden',
    border: `1px solid ${BORDER_GRAY}`,
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: TEXT_MID,
    borderBottom: `2px solid ${BORDER_GRAY}`,
    background: BG_LIGHT,
  },
  td: {
    padding: '12px 16px',
    fontSize: 14,
    borderBottom: `1px solid ${BG_LIGHT}`,
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: TEXT_MID,
    fontSize: 15,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    fontSize: 16,
    color: TEXT_MID,
  },
  error: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#c0392b',
    fontSize: 15,
  },
};

export default function TestDashboardPage() {
  const { domain } = useParams();
  const navigate = useNavigate();

  const [tests, setTests] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      testPrepAPI.listPracticeTests(domain).catch(() => null),
      testPrepAPI.getScoreHistory(domain).catch(() => null),
    ]).then(([testsRes, histRes]) => {
      if (cancelled) return;
      if (testsRes?.data) {
        const d = testsRes.data.data || testsRes.data;
        setTests(d.tests || d || []);
      }
      if (histRes?.data) {
        const d = histRes.data.data || histRes.data;
        setHistory(d.scores || d.history || d || []);
      }
      setLoading(false);
    }).catch((err) => {
      if (!cancelled) {
        setError('Failed to load practice tests. Please try again.');
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [domain]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading practice tests...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.backBtn} onClick={() => navigate(`/domain/${domain}`)}>
            ← Back
          </button>
          <h1 style={styles.headerTitle}>{domain.toUpperCase()} Practice Tests</h1>
        </div>
      </div>

      <div style={styles.content}>
        <h2 style={styles.sectionTitle}>Available Tests</h2>

        {tests.length === 0 ? (
          <div style={styles.emptyState}>
            No practice tests available yet. Check back soon!
          </div>
        ) : (
          <div style={styles.grid}>
            {tests.map((test) => (
              <div
                key={test.test_id}
                style={styles.card}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = ACCENT;
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(30,58,95,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = BORDER_GRAY;
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                }}
              >
                <div style={styles.cardTitle}>
                  {test.display_name || test.title || `Practice Test`}
                </div>
                <div style={styles.cardMeta}>
                  <span style={styles.metaBadge}>
                    {test.total_sections || test.sections?.length || '—'} sections
                  </span>
                  <span style={styles.metaBadge}>
                    {test.total_questions || '—'} questions
                  </span>
                  <span style={styles.metaBadge}>
                    ~{(test.total_sections || test.sections?.length || 4) * 35} min
                  </span>
                </div>
                <button
                  style={styles.startBtn}
                  onClick={() => navigate(`/tests/${domain}/session/${test.test_id}`)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = ACCENT_LIGHT; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ACCENT; }}
                >
                  Start Test
                </button>
              </div>
            ))}
          </div>
        )}

        {history.length > 0 && (
          <>
            <h2 style={{ ...styles.sectionTitle, marginTop: 48 }}>Score History</h2>
            <table style={styles.historyTable}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Score</th>
                  <th style={styles.th}>Percentile</th>
                  <th style={styles.th}>Raw</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry, i) => (
                  <tr key={entry.session_id || i}>
                    <td style={styles.td}>
                      {entry.scored_at || entry.date
                        ? new Date(entry.scored_at || entry.date).toLocaleDateString()
                        : '—'}
                    </td>
                    <td style={{ ...styles.td, fontWeight: 700, color: ACCENT }}>
                      {entry.scaled_score ?? '—'}
                    </td>
                    <td style={styles.td}>
                      {entry.percentile != null ? `${entry.percentile}th` : '—'}
                    </td>
                    <td style={styles.td}>
                      {entry.raw_score != null && entry.total_questions
                        ? `${entry.raw_score}/${entry.total_questions}`
                        : '—'}
                    </td>
                    <td style={styles.td}>
                      {entry.session_id && (
                        <button
                          style={{
                            background: 'none',
                            border: `1px solid ${ACCENT}`,
                            color: ACCENT,
                            padding: '4px 12px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                          onClick={() => navigate(`/tests/${domain}/report/${entry.session_id}`)}
                        >
                          View Report
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
